import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import { getImageUrl } from '../utils/imageUtils';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  CardMedia,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  useTheme,
  useMediaQuery,
  alpha,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Menu,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  LocationOn,
  Category,
  Event,
  Clear,
  Map as MapIcon,
  ViewList as ListIcon,
  Palette as PaletteIcon,
} from '@mui/icons-material';
import { apiWithAuth } from '../services/api';
import { format, parseISO } from 'date-fns';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MAPBOX_ACCESS_TOKEN } from '../config/mapbox';

// Set the Mapbox token globally
mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

const FindEvents = () => {
  const theme = useTheme();
  const history = useHistory();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [viewMode, setViewMode] = useState('list');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    categories: [],
    locations: [],
    organizers: [],
    dateRange: [0, 30],
    priceRange: [0, 1000],
  });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [mapStyle, setMapStyle] = useState('outdoors-v12'); // Options: 'light-v11', 'dark-v11', 'outdoors-v12', 'streets-v12'
  const [styleMenuAnchor, setStyleMenuAnchor] = useState(null);

  const mapStyles = [
    { value: 'outdoors-v12', label: 'Outdoors', description: 'Natural, great for markets' },
    { value: 'dark-v11', label: 'Dark', description: 'Modern dark theme - makes pins pop!' },
    { value: 'light-v11', label: 'Light', description: 'Clean minimal gray' },
    { value: 'streets-v12', label: 'Streets', description: 'Classic street map' },
  ];

  const { data: events = [], isLoading, error } = useQuery('events', async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await apiWithAuth(token).get('/events/');
      console.log('Raw API Response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching events:', error);
      throw error;
    }
  });

  // Filter events based on search and filters
  const filteredEvents = useMemo(() => {
    if (!events || !Array.isArray(events)) return [];
    
    const filtered = events.filter(event => {
      if (!event) return false;

      // Only show upcoming events
      const eventDate = new Date(event.date);
      const isUpcoming = eventDate > new Date();

      // Only apply search filter if there's a search query
      const matchesSearch = !searchQuery || 
        (event.title && event.title.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (event.description && event.description.toLowerCase().includes(searchQuery.toLowerCase()));

      // Only apply location filter if locations are selected
      const matchesLocation = !filters.locations.length || 
        (event.location && filters.locations.includes(event.location));

      return isUpcoming && matchesSearch && matchesLocation;
    });

    // Sort by date
    return filtered.sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [events, searchQuery, filters]);

  // Add this to help debug the component state
  useEffect(() => {
    console.log('Component state:', {
      isLoading,
      error,
      eventsCount: events?.length,
      eventsSample: events?.[0],
      filteredEventsCount: filteredEvents?.length,
      searchQuery,
      filters
    });
  }, [isLoading, error, events, filteredEvents, searchQuery, filters]);

  // Store markers to clean them up properly
  const markersRef = useRef([]);

  // Add custom styles for markers and popups
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      /* Modern, clean event marker */
      .event-marker {
        width: 40px;
        height: 40px;
        border-radius: 50% 50% 50% 0;
        background: ${theme.palette.primary.main};
        border: 3px solid white;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        transform: rotate(-45deg);
        cursor: pointer;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .event-marker:hover {
        transform: rotate(-45deg) scale(1.1);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        z-index: 1000;
      }

      .event-marker-inner {
        transform: rotate(45deg);
        color: white;
        font-size: 20px;
        font-weight: bold;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .event-popup {
        width: 100%;
        max-width: 320px;
        background: white;
        border-radius: 16px;
        overflow: hidden;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
      }

      .popup-header {
        position: relative;
        height: 180px;
        overflow: hidden;
      }

      .popup-header img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.3s ease;
      }

      .popup-header::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 60px;
        background: linear-gradient(to top, rgba(0,0,0,0.3), transparent);
      }

      .popup-date {
        position: absolute;
        top: 12px;
        right: 12px;
        background: rgba(255, 255, 255, 0.98);
        backdrop-filter: blur(10px);
        padding: 10px 14px;
        border-radius: 12px;
        text-align: center;
        box-shadow: 0 4px 16px rgba(0,0,0,0.15);
        border: 2px solid ${theme.palette.primary.main};
        min-width: 60px;
      }

      .popup-date .day {
        display: block;
        font-size: 24px;
        font-weight: 700;
        color: ${theme.palette.primary.main};
        line-height: 1;
        margin-bottom: 2px;
      }

      .popup-date .month {
        display: block;
        font-size: 12px;
        color: #666;
        text-transform: uppercase;
        font-weight: 600;
        letter-spacing: 0.5px;
      }

      .popup-content {
        padding: 20px;
        background: white;
      }

      .popup-content h3 {
        margin: 0 0 16px 0;
        font-size: 20px;
        color: #1a1a1a;
        font-weight: 700;
        line-height: 1.3;
      }

      .popup-details {
        margin-bottom: 18px;
        display: flex;
        flex-direction: column;
        gap: 10px;
      }

      .detail-item {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        padding: 10px;
        background: #f8f9fa;
        border-radius: 8px;
        font-size: 13px;
        color: #444;
      }

      .detail-item-label {
        font-weight: 600;
        color: #666;
        min-width: 80px;
        font-size: 12px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .detail-item-value {
        flex: 1;
        color: #1a1a1a;
        font-weight: 500;
      }

      .popup-actions {
        display: flex;
        gap: 10px;
      }

      .popup-actions button {
        flex: 1;
        padding: 12px 20px;
        border: none;
        border-radius: 10px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
      }

      .view-details {
        background: ${theme.palette.primary.main};
        color: white;
        box-shadow: 0 4px 12px ${alpha(theme.palette.primary.main, 0.3)};
      }

      .view-details:hover {
        background: ${theme.palette.primary.dark};
        transform: translateY(-2px);
        box-shadow: 0 6px 16px ${alpha(theme.palette.primary.main, 0.4)};
      }

      .mapboxgl-popup-close-button {
        padding: 8px !important;
        font-size: 20px !important;
        color: #666 !important;
        background: rgba(255, 255, 255, 0.95) !important;
        border-radius: 50% !important;
        width: 32px !important;
        height: 32px !important;
        line-height: 32px !important;
        text-align: center !important;
        margin: 12px !important;
        border: 2px solid ${theme.palette.primary.main} !important;
        transition: all 0.3s ease !important;
      }

      .mapboxgl-popup-close-button:hover {
        background: white !important;
        color: ${theme.palette.primary.main} !important;
        transform: rotate(90deg);
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, [theme]);

  // Helper function to get price display from event
  const getPriceDisplay = (event) => {
    if (event.table_tiers && Array.isArray(event.table_tiers) && event.table_tiers.length > 0) {
      const prices = event.table_tiers.map(tier => tier.fee || 0).filter(fee => fee > 0);
      if (prices.length === 0) return 'Free';
      if (prices.length === 1) return `$${prices[0]}`;
      const minPrice = Math.min(...prices);
      const maxPrice = Math.max(...prices);
      return minPrice === maxPrice ? `$${minPrice}` : `$${minPrice} - $${maxPrice}`;
    }
    return event.fee ? `$${event.fee}` : 'Free';
  };

  // Update markers when filteredEvents change
  const updateMarkers = useCallback(() => {
    if (!map.current || !map.current.loaded()) return;

    // Remove existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    // Add markers for each event
    filteredEvents.forEach((event) => {
      if (!event.latitude || !event.longitude) return;

      // Create simple, modern marker
      const el = document.createElement('div');
      el.className = 'event-marker';
      el.innerHTML = '<div class="event-marker-inner">🛒</div>';

      // Create popup content
      const popup = new mapboxgl.Popup({ 
        offset: 20,
        maxWidth: '300px',
        anchor: 'bottom',
        closeButton: true,
        closeOnClick: false
      });

      // Get price display
      const priceDisplay = getPriceDisplay(event);
      const imageUrl = event.image_url ? getImageUrl(event.image_url) : 'https://source.unsplash.com/random/300x200/?market';

      // Create popup content element
      const popupContent = document.createElement('div');
      popupContent.className = 'event-popup';
      popupContent.innerHTML = `
        <div class="popup-header">
          <img src="${imageUrl}" alt="${event.title}" />
          <div class="popup-date">
            <span class="day">${format(new Date(event.date), 'd')}</span>
            <span class="month">${format(new Date(event.date), 'MMM')}</span>
          </div>
        </div>
        <div class="popup-content">
          <h3>${event.title}</h3>
          <div class="popup-details">
            <div class="detail-item">
              <span class="detail-item-label">Date</span>
              <span class="detail-item-value">${format(new Date(event.date), 'MMM d, yyyy • h:mm a')}</span>
            </div>
            <div class="detail-item">
              <span class="detail-item-label">Location</span>
              <span class="detail-item-value">${event.location}</span>
            </div>
            <div class="detail-item">
              <span class="detail-item-label">Fee</span>
              <span class="detail-item-value" style="color: #2e7d32; font-weight: 600;">${priceDisplay}</span>
            </div>
          </div>
          <div class="popup-actions">
            <button class="view-details">View Details</button>
          </div>
        </div>
      `;

      // Add click handler to the button
      const viewDetailsButton = popupContent.querySelector('.view-details');
      if (viewDetailsButton) {
        viewDetailsButton.addEventListener('click', () => {
          history.push(`/event/${event.event_id}`);
        });
      }

      popup.setDOMContent(popupContent);

      // Create and add marker
      const marker = new mapboxgl.Marker({
        element: el,
        anchor: 'bottom'
      })
        .setLngLat([event.longitude, event.latitude])
        .setPopup(popup)
        .addTo(map.current);

      // Add click handler to marker to highlight corresponding list item
      marker.getElement().addEventListener('click', () => {
        setSelectedEvent(event.event_id);
        // Scroll to the event card in the list container only
        setTimeout(() => {
          const eventCard = document.getElementById(`event-card-${event.event_id}`);
          const listContainer = document.getElementById('event-list-container');
          if (eventCard && listContainer) {
            const cardRect = eventCard.getBoundingClientRect();
            const containerRect = listContainer.getBoundingClientRect();
            const scrollTop = listContainer.scrollTop;
            const cardTop = cardRect.top - containerRect.top + scrollTop;
            const centerOffset = (containerRect.height - cardRect.height) / 2;
            
            listContainer.scrollTo({
              top: cardTop - centerOffset,
              behavior: 'smooth'
            });
          }
        }, 100);
      });

      markersRef.current.push(marker);
    });

    // Fit map to show all markers if there are any
    if (markersRef.current.length > 0 && map.current) {
      const bounds = new mapboxgl.LngLatBounds();
      markersRef.current.forEach(marker => {
        bounds.extend(marker.getLngLat());
      });
      map.current.fitBounds(bounds, {
        padding: { top: 50, bottom: 50, left: 50, right: 50 },
        maxZoom: 14
      });
    }
  }, [filteredEvents, history]);

  // Initialize map - only once when component mounts
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    const initializeMap = () => {
      try {
        map.current = new mapboxgl.Map({
          container: mapContainer.current,
          style: `mapbox://styles/mapbox/${mapStyle}`,
          center: [-114.0719, 51.0447], // Calgary coordinates
          zoom: 11,
          attributionControl: false
        });

        // Add navigation controls
        map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
        map.current.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-right');

        // Wait for map to be ready before adding markers
        map.current.on('load', () => {
          console.log('Map loaded successfully');
          updateMarkers();
        });

        // If map is already loaded, add markers immediately
        if (map.current.loaded()) {
          updateMarkers();
        }
      } catch (error) {
        console.error('Error initializing map:', error);
      }
    };

    // Small delay to ensure container is ready
    const timer = setTimeout(initializeMap, 100);

    return () => {
      clearTimeout(timer);
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
      // Clean up markers
      markersRef.current.forEach(marker => marker.remove());
      markersRef.current = [];
    };
  }, [updateMarkers]);

  // Update markers when filteredEvents change
  useEffect(() => {
    if (map.current && map.current.loaded()) {
      updateMarkers();
    }
  }, [updateMarkers]);

  // Handle map style changes
  useEffect(() => {
    if (map.current && map.current.loaded()) {
      map.current.setStyle(`mapbox://styles/mapbox/${mapStyle}`);
      map.current.once('style.load', () => {
        updateMarkers();
      });
    }
  }, [mapStyle, updateMarkers]);

  // Helper function to get coordinates from location
  const getCoordinatesFromLocation = (location) => {
    // Map of locations to coordinates
    const locationCoordinates = {
      'calgary': [-114.0719, 51.0447],
      'edmonton': [-113.4909, 53.5444],
      'vancouver': [-123.1207, 49.2827],
      'victoria': [-123.3656, 48.4284],
      'kelowna': [-119.4960, 49.8880],
      'saskatoon': [-106.6700, 52.1332],
      'regina': [-104.6177, 50.4547],
      'winnipeg': [-97.1385, 49.8951]
    };

    // Convert location to lowercase and remove any extra spaces
    const normalizedLocation = location.toLowerCase().trim();
    
    // Check if we have coordinates for this location
    if (locationCoordinates[normalizedLocation]) {
      return locationCoordinates[normalizedLocation];
    }

    // If location contains any of our known cities, use those coordinates
    for (const [city, coords] of Object.entries(locationCoordinates)) {
      if (normalizedLocation.includes(city)) {
        return coords;
      }
    }

    return null;
  };

  // Handle event selection
  const handleEventSelect = (event) => {
    console.log('Event selected:', event);
    setSelectedEvent(event.event_id);
    
    // If event has coordinates, fly to it and open popup
    if (event.latitude && event.longitude && map.current) {
      map.current.flyTo({
        center: [event.longitude, event.latitude],
        zoom: 14,
        duration: 1500
      });
      
      // Find and open the marker popup
      setTimeout(() => {
        const marker = markersRef.current.find(m => {
          const lngLat = m.getLngLat();
          return Math.abs(lngLat.lng - event.longitude) < 0.0001 && 
                 Math.abs(lngLat.lat - event.latitude) < 0.0001;
        });
        if (marker) {
          marker.togglePopup();
        }
      }, 1600);
    } else {
      // Fallback to location-based coordinates
      const coordinates = getCoordinatesFromLocation(event.location);
      if (map.current && coordinates) {
        map.current.flyTo({
          center: coordinates,
          zoom: 14,
          duration: 1500
        });
      }
    }
  };

  // Western Canada locations
  const locations = [
    { value: 'calgary', label: 'Calgary, AB' },
    { value: 'edmonton', label: 'Edmonton, AB' },
    { value: 'vancouver', label: 'Vancouver, BC' },
    { value: 'victoria', label: 'Victoria, BC' },
    { value: 'kelowna', label: 'Kelowna, BC' },
    { value: 'saskatoon', label: 'Saskatoon, SK' },
    { value: 'regina', label: 'Regina, SK' },
    { value: 'winnipeg', label: 'Winnipeg, MB' }
  ];

  // Filter drawer content
  const FilterDrawer = () => (
    <Drawer
      anchor="left"
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      PaperProps={{
        sx: { width: 300, p: 2 }
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Filters</Typography>
        <IconButton onClick={() => setDrawerOpen(false)}>
          <Clear />
        </IconButton>
      </Box>
      <Divider sx={{ mb: 2 }} />

      <List>
        <ListItem>
          <ListItemIcon>
            <Category />
          </ListItemIcon>
          <ListItemText primary="Categories" />
        </ListItem>
        <ListItem>
          <FormControl fullWidth>
        <Select
          multiple
          value={filters.categories}
          onChange={(e) => setFilters({ ...filters, categories: e.target.value })}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
              ))}
            </Box>
          )}
        >
              <MenuItem value={1}>Food</MenuItem>
              <MenuItem value={2}>Crafts</MenuItem>
              <MenuItem value={3}>Art</MenuItem>
              <MenuItem value={4}>Music</MenuItem>
              <MenuItem value={5}>Other</MenuItem>
        </Select>
      </FormControl>
        </ListItem>
        
        <Divider sx={{ my: 2 }} />
        
        <ListItem>
          <ListItemIcon>
            <LocationOn />
          </ListItemIcon>
          <ListItemText primary="Locations" />
        </ListItem>
        <ListItem>
          <FormControl fullWidth>
        <Select
          multiple
          value={filters.locations}
          onChange={(e) => setFilters({ ...filters, locations: e.target.value })}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
              ))}
            </Box>
          )}
        >
              {locations.map((location) => (
                <MenuItem key={location.value} value={location.value}>
                  {location.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
        </ListItem>
        
        <Divider sx={{ my: 2 }} />
        
        <ListItem>
          <ListItemIcon>
            <Event />
          </ListItemIcon>
          <ListItemText primary="Date Range" />
        </ListItem>
        <ListItem>
          <Box sx={{ width: '100%', px: 2 }}>
      <Slider
        value={filters.dateRange}
        onChange={(e, newValue) => setFilters({ ...filters, dateRange: newValue })}
        valueLabelDisplay="auto"
        min={0}
              max={90}
              marks={[
                { value: 0, label: 'Today' },
                { value: 30, label: '30 days' },
                { value: 60, label: '60 days' },
                { value: 90, label: '90 days' }
              ]}
      />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="caption">Today</Typography>
              <Typography variant="caption">{filters.dateRange[1]} days</Typography>
            </Box>
          </Box>
        </ListItem>
      </List>
    </Drawer>
  );

  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
        </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
          <Alert severity="error">
          Error loading events: {error.message}
          </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', position: 'relative', display: 'flex' }}>
      {/* Left Side - Map */}
      <Box sx={{ flex: 1, position: 'relative' }}>
        <Paper 
          sx={{ 
            height: '100%',
            width: '100%',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <div 
            ref={mapContainer} 
            style={{ 
              width: '100%',
              height: '100%'
            }} 
          />
          {/* Map Style Selector */}
          <Box
            sx={{
              position: 'absolute',
              top: 110,
              right: 16,
              zIndex: 1000,
            }}
          >
            <Tooltip title="Change map style">
              <IconButton
                onClick={(e) => setStyleMenuAnchor(e.currentTarget)}
                sx={{
                  bgcolor: 'background.paper',
                  boxShadow: 2,
                  '&:hover': {
                    bgcolor: 'background.paper',
                    boxShadow: 4,
                  },
                }}
              >
                <PaletteIcon />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={styleMenuAnchor}
              open={Boolean(styleMenuAnchor)}
              onClose={() => setStyleMenuAnchor(null)}
              PaperProps={{
                sx: {
                  minWidth: 200,
                  mt: 1,
                }
              }}
            >
              {mapStyles.map((style) => (
                <MenuItem
                  key={style.value}
                  selected={mapStyle === style.value}
                  onClick={() => {
                    setMapStyle(style.value);
                    setStyleMenuAnchor(null);
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={mapStyle === style.value ? 600 : 400}>
                      {style.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {style.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Paper>
      </Box>

      {/* Right Side - Event List */}
      <Paper 
        sx={{ 
          width: { xs: '100%', md: 420 },
          height: '100%',
          overflow: 'hidden',
          borderLeft: '1px solid rgba(0, 0, 0, 0.12)',
          display: 'flex',
          flexDirection: 'column',
          borderTopLeftRadius: { xs: 0, md: 12 },
          borderBottomLeftRadius: { xs: 0, md: 12 }
        }}
      >
        {/* Header / Search and Filter Bar */}
        <Box sx={{ p: 2, borderBottom: '1px solid rgba(0, 0, 0, 0.12)', background: 'linear-gradient(180deg, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0) 100%)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">Upcoming Markets</Typography>
            <ToggleButtonGroup
              size="small"
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
            >
              <ToggleButton value="list"><ListIcon fontSize="small" /></ToggleButton>
              <ToggleButton value="map"><MapIcon fontSize="small" /></ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <TextField
            fullWidth
            placeholder="Search events..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="primary" fontSize="small" />
                </InputAdornment>
              ),
              sx: { 
                borderRadius: 2,
                '& fieldset': { border: 'none' },
                '&:hover fieldset': { border: '1px solid rgba(0, 0, 0, 0.1)' },
                '&.Mui-focused fieldset': { border: '1px solid rgba(0, 0, 0, 0.1)' }
              }
            }}
          />
          <Button
            fullWidth
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={() => setDrawerOpen(true)}
            size="small"
            sx={{ 
              mt: 1,
              borderRadius: 2,
              borderColor: 'rgba(0, 0, 0, 0.1)',
              color: 'text.primary',
              '&:hover': {
                borderColor: 'rgba(0, 0, 0, 0.2)',
                backgroundColor: 'rgba(0, 0, 0, 0.02)'
              }
            }}
          >
            Filters
          </Button>
        </Box>

        {/* Event List */}
        <Box id="event-list-container" sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ m: 2 }}>
              Error loading events: {error.message}
            </Alert>
          ) : filteredEvents.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No upcoming events found
              </Typography>
            </Box>
          ) : (
            filteredEvents.map((event) => (
              <Card 
                key={event.id}
                id={`event-card-${event.event_id}`}
                sx={{ 
                  mb: 2,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: selectedEvent === event.event_id 
                    ? `3px solid ${theme.palette.primary.main}` 
                    : '1px solid rgba(0,0,0,0.06)',
                  borderRadius: 3,
                  boxShadow: selectedEvent === event.event_id 
                    ? `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}` 
                    : 'none',
                  transform: selectedEvent === event.event_id ? 'scale(1.02)' : 'scale(1)',
                  bgcolor: selectedEvent === event.event_id 
                    ? alpha(theme.palette.primary.main, 0.04) 
                    : 'background.paper',
                  '&:hover': {
                    transform: selectedEvent === event.event_id ? 'scale(1.02)' : 'translateY(-2px)',
                    boxShadow: selectedEvent === event.event_id 
                      ? `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}` 
                      : '0 10px 24px rgba(0,0,0,0.12)',
                    borderColor: 'primary.main'
                  }
                }}
                onClick={() => handleEventSelect(event)}
              >
                <Box sx={{ position: 'relative' }}>
                  <CardMedia
                    component="img"
                    height="160"
                    image={event.image_url ? getImageUrl(event.image_url) : 'https://source.unsplash.com/random/400x200/?market'}
                    alt={event.title}
                    sx={{ 
                      objectFit: 'cover',
                      borderBottom: '1px solid rgba(0,0,0,0.08)'
                    }}
                  />
                  <Chip
                    label={format(new Date(event.date), 'MMM d, yyyy')}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      bgcolor: 'rgba(255,255,255,0.95)',
                      backdropFilter: 'blur(6px)',
                      fontWeight: 600
                    }}
                  />
                </Box>
                <CardContent sx={{ p: 2.25 }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 700,
                      color: 'text.primary',
                      mb: 1
                    }}
                  >
                    {event.title}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mb: 1
                    }}
                  >
                    <LocationOn fontSize="small" />
                    {event.location.split(',')[0]}
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: 1.5,
                    mt: 2
                  }}>
                    {event.table_tiers && Array.isArray(event.table_tiers) && event.table_tiers.length > 0 ? (
                      <Box>
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}
                        >
                          Vendor Fees:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {event.table_tiers.map((tier, idx) => (
                            <Chip
                              key={idx}
                              label={`${tier.name || 'Tier'}: $${tier.fee || 0}`}
                              size="small"
                              sx={{
                                fontSize: '0.7rem',
                                height: '24px',
                                bgcolor: tier.fee === 0 ? 'success.light' : 'primary.light',
                                color: tier.fee === 0 ? 'success.dark' : 'primary.dark',
                                fontWeight: 600
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    ) : (
                      <Typography 
                        variant="subtitle2" 
                        color="primary"
                        sx={{ fontWeight: 700 }}
                      >
                        {event.fee ? `$${event.fee}` : 'Free'}
                      </Typography>
                    )}
                    <Button 
                      size="small" 
                      variant="contained"
                      fullWidth
                      onClick={(e) => {
                        e.stopPropagation();
                        history.push(`/event/${event.event_id}`);
                      }}
                      sx={{ 
                        borderRadius: 2,
                        textTransform: 'none',
                        mt: 0.5
                      }}
                    >
                      View Details
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      </Paper>

      {/* Filter Drawer */}
      <FilterDrawer />
    </Box>
  );
};

export default FindEvents; 